import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Edit, Trash2, User } from 'lucide-react';

const API_BASE = '/.netlify/functions';

const CommentsSection = ({
  listingId,
  user,
  isAuthenticated,
  hasSubscription,
  getValidAccessToken,
  isUserAdmin
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [listingId]);

  const fetchComments = async (cursor = null) => {
    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      let url = `${API_BASE}/marketplace-comments?listingId=${listingId}&limit=20`;
      if (cursor) {
        url += `&cursor=${encodeURIComponent(cursor)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch comments');

      const data = await response.json();

      if (cursor) {
        setComments(prev => [...prev, ...data.comments]);
      } else {
        setComments(data.comments);
      }
      setNextCursor(data.nextCursor);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    const optimisticComment = {
      id: `temp_${Date.now()}`,
      listing_id: listingId,
      user_id: user?.id,
      display_name: user?.firstName || user?.email?.split('@')[0] || 'You',
      body: newComment.trim(),
      created_at: new Date().toISOString(),
      edited_at: null
    };

    // Optimistic update
    setComments(prev => [...prev, optimisticComment]);
    setTotalCount(prev => prev + 1);
    const savedComment = newComment;
    setNewComment('');

    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/marketplace-comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ listingId, body: savedComment.trim() })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to post comment');
      }

      const created = await response.json();
      // Replace optimistic with real
      setComments(prev => prev.map(c => c.id === optimisticComment.id ? created : c));
    } catch (err) {
      console.error('Error posting comment:', err);
      // Revert optimistic update
      setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
      setTotalCount(prev => prev - 1);
      setNewComment(savedComment);
      alert(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const editComment = async (commentId) => {
    if (!editBody.trim()) return;

    const originalComment = comments.find(c => c.id === commentId);

    // Optimistic update
    setComments(prev => prev.map(c =>
      c.id === commentId ? { ...c, body: editBody.trim(), edited_at: new Date().toISOString() } : c
    ));
    setEditingId(null);

    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/marketplace-comments`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ commentId, body: editBody.trim() })
      });

      if (!response.ok) throw new Error('Failed to edit comment');

      const updated = await response.json();
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
    } catch (err) {
      console.error('Error editing comment:', err);
      // Revert
      if (originalComment) {
        setComments(prev => prev.map(c => c.id === commentId ? originalComment : c));
      }
      alert('Failed to edit comment');
    }
  };

  const deleteComment = async (commentId) => {
    if (!confirm('Delete this comment?')) return;

    const originalComments = [...comments];
    const originalTotal = totalCount;

    // Optimistic update
    setComments(prev => prev.filter(c => c.id !== commentId));
    setTotalCount(prev => prev - 1);

    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/marketplace-comments`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ commentId })
      });

      if (!response.ok) throw new Error('Failed to delete comment');
    } catch (err) {
      console.error('Error deleting comment:', err);
      // Revert
      setComments(originalComments);
      setTotalCount(originalTotal);
      alert('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      submitComment();
    }
  };

  return (
    <div className="space-y-6">
      {/* Comment input */}
      {isAuthenticated && hasSubscription ? (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment..."
            rows={3}
            maxLength={2000}
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">{newComment.length}/2000</span>
            <button
              onClick={submitComment}
              disabled={submitting || !newComment.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Send className="h-4 w-4" />
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      ) : isAuthenticated && !hasSubscription ? (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 text-center text-gray-400">
          <a href="/#pricing" className="text-blue-400 hover:underline">Subscribe</a> to post comments
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 text-center text-gray-400">
          Sign in to post comments
        </div>
      )}

      {/* Comments list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-400">{error}</div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="h-8 w-8 mx-auto mb-2" />
          <p>No comments yet. Be the first!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-white font-medium">{comment.display_name}</span>
                  <span className="text-gray-500">{formatDate(comment.created_at)}</span>
                  {comment.edited_at && (
                    <span className="text-gray-600 text-xs">(edited)</span>
                  )}
                </div>
                {user && (comment.user_id === user.id || isUserAdmin) && (
                  <div className="flex gap-2">
                    {comment.user_id === user.id && (
                      <button
                        onClick={() => { setEditingId(comment.id); setEditBody(comment.body); }}
                        className="text-gray-500 hover:text-blue-400 transition-colors"
                        title="Edit comment"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteComment(comment.id)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title="Delete comment"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {editingId === comment.id ? (
                <div className="mt-2">
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    className="w-full bg-gray-800 text-white rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    maxLength={2000}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => editComment(comment.id)}
                      disabled={!editBody.trim()}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-gray-300 text-sm whitespace-pre-wrap">{comment.body}</p>
              )}
            </div>
          ))}

          {/* Load More */}
          {nextCursor && (
            <button
              onClick={() => fetchComments(nextCursor)}
              disabled={loadingMore}
              className="w-full py-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              {loadingMore ? 'Loading...' : `Load more comments (${comments.length} of ${totalCount})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
